import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { Button, Dropdown, EmptyView, Input, Loading, Modal, Radio, Trans } from '@/components'
import { PortProtocol } from '@/consts/machine'
import { useDomain } from '@/contexts/DomainContext'
import { getEIPListPath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { useNATListQuery } from '@/hooks/useNATListQuery'
import type { CreateInstanceBoundNATInfo } from '@/pages/create-instance/types'
import { debounce } from '@/utils/debounce'

const MAX_PORT = 65535

function formatBandwidth(bandwidth: CreateInstanceBoundNATInfo['bandwidth'], sharedLabel: string) {
  if (!bandwidth) return '-'
  if (bandwidth.bandwidthType === 0) return sharedLabel

  const mbps = bandwidth.rateBitsPerSec / 1_000_000
  const formattedMbps = Number.isInteger(mbps) ? mbps : Number(mbps.toFixed(2))
  return `${formattedMbps}Mbps`
}

function parsePortList(value: string) {
  const tokens = value.split(',').map((port) => port.trim())
  if (tokens.length === 0 || tokens.some((port) => port === '')) return []

  const ports = tokens.map(Number)
  const hasInvalidPort = ports.some(
    (port) => !Number.isInteger(port) || port < 1 || port > MAX_PORT,
  )
  if (hasInvalidPort) return []

  const uniquePorts = new Set(ports)
  if (uniquePorts.size !== ports.length) return []

  return ports
}

function getPortListError(value: string): 'range' | 'duplicate' | null {
  if (value.trim() === '') return null

  const tokens = value.split(',').map((port) => port.trim())
  if (tokens.some((port) => port === '')) return 'range'

  const ports = tokens.map(Number)
  if (ports.some((port) => !Number.isInteger(port) || port < 1 || port > MAX_PORT)) return 'range'

  if (new Set(ports).size !== ports.length) return 'duplicate'

  return null
}

export function BindNATModal(props: {
  open: boolean
  domain?: number
  boundNAT?: CreateInstanceBoundNATInfo
  onClose: () => void
  onOk: (selectedIp?: CreateInstanceBoundNATInfo) => void
}) {
  const { open, domain, boundNAT, onClose, onOk } = props
  const navigate = useNavigate()
  const ctx = useUnifiedContext()
  const { t } = useTranslation()
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null)
  const { domainInfos } = useDomain()
  const eipListPath = getEIPListPath(ctx)
  const [protocol, setProtocol] = useState<PortProtocol>(PortProtocol.SSH)
  const [destinationPortInput, setDestinationPortInput] = useState('')
  const [checkingPorts, setCheckingPorts] = useState(false)
  const [conflictPorts, setConflictPorts] = useState<number[]>([])
  // Bumped on every input/NAT change so stale async check results are discarded
  const checkSeqRef = useRef(0)

  const debouncedCheckPorts = useRef(
    debounce(async (resID: string, ports: number[], seq: number) => {
      try {
        const results = await Promise.all(
          ports.map(async (port) => {
            const res = await API.checkNATPort({ res_id: resID, listen_port: port })
            return res.code === 194 ? port : null
          }),
        )
        if (seq !== checkSeqRef.current) return
        setConflictPorts(results.filter((port): port is number => port !== null))
      } catch (error) {
        console.error('Error checking ports:', error)
      } finally {
        if (seq === checkSeqRef.current) {
          setCheckingPorts(false)
        }
      }
    }, 500),
  ).current

  useEffect(() => {
    checkSeqRef.current += 1
    const seq = checkSeqRef.current
    setConflictPorts([])

    const ports = parsePortList(destinationPortInput)
    if (!open || !selectedRowKey || ports.length === 0) {
      debouncedCheckPorts.cancel()
      setCheckingPorts(false)
      return
    }

    setCheckingPorts(true)
    debouncedCheckPorts(selectedRowKey, ports, seq)
  }, [open, selectedRowKey, destinationPortInput, debouncedCheckPorts])

  // Cancel pending check when component unmounts
  useEffect(() => {
    return () => {
      debouncedCheckPorts.cancel()
    }
  }, [debouncedCheckPorts])

  const queryEnabled = open && domain !== undefined

  const { networks: nats, isLoading: natsLoading } = useNATListQuery({
    params: {
      network_type: 6,
      regions: domain !== undefined ? [domain] : [],
      region_filter_criteria: 0,
      page: 1,
      per_page: 10,
    },
    enabled: queryEnabled,
  })

  const { data: hasRegionStock = false, isLoading: stockLoading } = useQuery({
    queryKey: ['eipStock', ctx.mode, ctx.teamResID, ctx.memberResID, domain],
    queryFn: async ({ signal }) => {
      const res = await API.getAvailableEIPs(
        { page: 1, per_page: 1, region: domain!, region_filter_criteria: 0 },
        { signal },
      )
      return res.code === 0 && !!res.eIPS && res.eIPS.length > 0
    },
    enabled: queryEnabled,
  })

  const loading = natsLoading || stockLoading

  // Reset selection when modal opens
  useEffect(() => {
    if (!open) return

    if (boundNAT) {
      setSelectedRowKey(boundNAT.resID)
      setProtocol(boundNAT.protocol ?? PortProtocol.SSH)
      setDestinationPortInput(boundNAT.destinationPorts?.join(',') ?? '')
      return
    }

    setSelectedRowKey(null)
    setProtocol(PortProtocol.SSH)
    setDestinationPortInput('')
  }, [open, boundNAT])

  const selectedNat = nats.find((nat) => nat.resID === selectedRowKey)
  const destinationPorts = parsePortList(destinationPortInput)
  const isDestinationPortValid = destinationPorts.length > 0
  const portListError = getPortListError(destinationPortInput)
  const showEmptyState = !loading && nats.length === 0
  const canAcquirePublicIP = showEmptyState && hasRegionStock

  const protocolOptions = [
    {
      value: PortProtocol.SSH,
      label: <Trans i18nKey="create.bindNATModal.customTCP">Custom TCP</Trans>,
    },
  ]

  return (
    <Modal
      className="w-[720px] shadow-[0px_64px_240px_0px_rgba(33,51,101,0.05),0px_20px_72px_0px_rgba(33,51,101,0.03),0px_8px_32px_0px_rgba(33,51,101,0.03),0px_4px_12px_0px_rgba(33,51,101,0.02)]"
      open={open}
      onClose={onClose}
    >
      <Modal.Header className="text-secondary t-sb">
        <Trans i18nKey="create.bindNATModal.title">Bind Public IP Address</Trans>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loading />
          </div>
        ) : showEmptyState ? (
          <EmptyView
            title={
              hasRegionStock ? (
                <Trans i18nKey="create.bindNATModal.noResourceTitle">
                  No Public IP Resources Found
                </Trans>
              ) : (
                <Trans i18nKey="create.bindNATModal.noDataTitle">No Public IP available</Trans>
              )
            }
            imageSize="small"
            className="h-[200px] p-0 text-center"
            content={
              hasRegionStock ? (
                <Trans i18nKey="create.bindNATModal.noResourceContent">
                  You have no Public IP addresses in this region.
                  <br />
                  Acquire one to continue.
                </Trans>
              ) : (
                <Trans
                  i18nKey="create.bindNATModal.noDataContent"
                  values={{
                    region: domainInfos.find((d) => d.domain === domain)?.displayName,
                  }}
                >
                  The Public IP address is currently unavailable in the JP-01 region while we
                  upgrade the service.
                </Trans>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full table-fixed overflow-hidden rounded-lg">
              <colgroup>
                <col className="w-[52px]" />
                <col />
                <col className="w-[112px]" />
                <col className="w-[96px]" />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>
                    <Trans i18nKey="create.bindPublicIpTableResId">ID</Trans>
                  </th>
                  <th>
                    <Trans i18nKey="create.bindPublicIpTableRegion">Region</Trans>
                  </th>
                  <th>
                    <Trans i18nKey="create.bindNATModal.bandwidth">Bandwidth</Trans>
                  </th>
                  <th className="!text-right">
                    <Trans i18nKey="create.bindPublicIpTableIp">Public IP Address</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {nats.map((nat) => (
                  <tr
                    key={nat.resID}
                    className={
                      'cursor-pointer' + (selectedRowKey === nat.resID ? ' bg-panel-gray-100' : '')
                    }
                    onClick={() => setSelectedRowKey(nat.resID)}
                  >
                    <td className="w-5 t-sm">
                      <Radio
                        checked={selectedRowKey === nat.resID}
                        onChange={() => setSelectedRowKey(nat.resID)}
                      />
                    </td>
                    <td className="t-sm">
                      <div className="text-primary t-sb">{nat.resID}</div>
                    </td>
                    <td className="t-sm">
                      <div className="text-primary t-sb">
                        {domainInfos.find((info) => info.domain === nat.domain)?.displayName ||
                          nat.domain}
                      </div>
                    </td>
                    <td className="t-sm">
                      <div className="text-primary t-sb">
                        {formatBandwidth(
                          nat.bandwidth,
                          t('create.bindNATModal.bandwidthShared', 'Shared'),
                        )}
                      </div>
                    </td>
                    <td className="t-sm text-right">
                      <div className="text-primary t-sb">{nat.name}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {nats.length > 0 && (
          <>
            <div className="mt-4">
              <div className="mb-2 text-primary t-sb">
                <Trans i18nKey="create.bindNATModal.protocol">Protocol</Trans>
              </div>
              <Dropdown
                options={protocolOptions}
                value={protocol}
                disabled={!selectedRowKey}
                onChange={(value) => setProtocol(value as PortProtocol)}
              />
            </div>
            <div className="mt-4">
              <div className="mb-2 text-primary t-sb">
                <Trans i18nKey="create.bindNATModal.destinationPort">Destination Port</Trans>
              </div>
              <Input
                type="text"
                value={destinationPortInput}
                min={1}
                max={MAX_PORT}
                disabled={!selectedRowKey}
                placeholder={t('create.bindNATModal.enterPorts', 'Enter ports, e.g. 22,8443')}
                className="h-11 border border-neutral-200 text-primary t-sb placeholder:text-tertiary"
                onChange={(e) => setDestinationPortInput(e.target.value)}
                validator={(value) => {
                  return parsePortList(value).length > 0
                }}
              />
              {portListError ? (
                <div className="mt-2 t-sm text-red-500">
                  {portListError === 'duplicate' ? (
                    <Trans i18nKey="create.bindNATModal.duplicatePorts">
                      Duplicate ports are not allowed.
                    </Trans>
                  ) : (
                    <Trans i18nKey="create.bindNATModal.invalidPortRange">
                      Ports must be integers between 1 and 65535, separated by commas.
                    </Trans>
                  )}
                </div>
              ) : checkingPorts ? (
                <div className="mt-2 t-sm text-secondary">
                  <Trans i18nKey="create.bindNATModal.checkingPorts">
                    Checking port availability...
                  </Trans>
                </div>
              ) : conflictPorts.length > 0 ? (
                <div className="mt-2 t-sm text-red-500">
                  {t('create.bindNATModal.portsInUse', {
                    defaultValue:
                      'Port {{ports}} already in use on this public IP. Please choose another port.',
                    ports: conflictPorts.join(', '),
                  })}
                </div>
              ) : null}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          onClick={() => {
            if (canAcquirePublicIP) {
              navigate(eipListPath)
              return
            }

            if (!selectedNat) {
              onOk(undefined)
              return
            }

            onOk({
              ...selectedNat,
              protocol,
              destinationPorts,
            })
          }}
          disabled={
            !canAcquirePublicIP &&
            (!selectedRowKey ||
              !selectedNat ||
              !isDestinationPortValid ||
              checkingPorts ||
              conflictPorts.length > 0)
          }
        >
          {canAcquirePublicIP ? (
            <Trans i18nKey="create.bindNATModal.acquireBtn">Acquire Public IP</Trans>
          ) : (
            <Trans i18nKey="create.bindNATModal.confirmBtn">Bind</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
